import * as React from "react";
import Parse from "parse";
import styled from "styled-components";
import {
  Card,
  DatePicker,
  Typography,
  Button,
  Input,
  Space,
  Tag,
  message,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import {
  AnalyticsData,
  GroupMode,
  downloadCsv,
  toCsv,
} from "./analyticsHelpers";
import { AnalyticsDashboardView } from "./AnalyticsDashboardView";

const { Text } = Typography;
const { RangePicker } = DatePicker;

const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f0f2f5 0%, #e6e9ef 100%);
`;

const DashboardContainer = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  padding: 32px 24px;
`;

const DashboardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 32px;
`;

const HeaderLeft = styled.div`
  h2 {
    margin: 0 0 4px 0;
    font-size: 28px;
    font-weight: 700;
    color: #1a1a2e;
  }
`;

const FilterBar = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
`;

const ActionButton = styled(Button)`
  border-radius: 8px;
`;

const LoginPage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
`;

const LoginBox = styled(Card)`
  width: 420px;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  border: none;

  .ant-card-head {
    text-align: center;
    border-bottom: none;
    padding-bottom: 0;
  }
  .ant-card-head-title {
    font-size: 22px;
    font-weight: 700;
    color: #1a1a2e;
  }
`;

interface ShareResponse {
  tenantTitle: string;
  data: AnalyticsData;
}

export const AnalyticsSharePublicRoute = React.memo(
  function AnalyticsSharePublicRoute() {
    const token = React.useMemo(
      () => new URLSearchParams(window.location.search).get("token") || "",
      []
    );

    const [password, setPassword] = React.useState("");
    const [authed, setAuthed] = React.useState(false);
    const [loginLoading, setLoginLoading] = React.useState(false);

    const [tenantTitle, setTenantTitle] = React.useState("");
    const [data, setData] = React.useState<AnalyticsData | null>(null);
    const [loading, setLoading] = React.useState(false);

    const [dateRange, setDateRange] = React.useState<[Dayjs, Dayjs]>([
      dayjs().subtract(30, "day"),
      dayjs(),
    ]);
    const [groupMode, setGroupMode] = React.useState<GroupMode>("day");

    const fetchData = React.useCallback(
      async (pw: string, range: [Dayjs, Dayjs], mode: GroupMode) => {
        setLoading(true);
        try {
          const result: ShareResponse = await Parse.Cloud.run(
            "analyticsShareGetData",
            {
              token,
              password: pw,
              from: range[0].startOf("day").toISOString(),
              to: range[1].endOf("day").toISOString(),
              groupMode: mode,
            }
          );
          setTenantTitle(result.tenantTitle);
          setData(result.data);
          return true;
        } catch (err: any) {
          console.error(err);
          message.error(err?.message || "Daten konnten nicht geladen werden");
          return false;
        } finally {
          setLoading(false);
        }
      },
      [token]
    );

    const handleLogin = async () => {
      if (!token) {
        message.error("Ungültiger Link");
        return;
      }
      if (!password) {
        message.warning("Bitte Passwort eingeben");
        return;
      }
      setLoginLoading(true);
      const ok = await fetchData(password, dateRange, groupMode);
      setLoginLoading(false);
      if (ok) setAuthed(true);
    };

    React.useEffect(() => {
      if (!authed) return;
      fetchData(password, dateRange, groupMode);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRange, groupMode]);

    if (!authed) {
      return (
        <LoginPage>
          <LoginBox title="Rechnungserklärer Analytics">
            {!token ? (
              <Text type="danger">
                Dieser Link ist ungültig. Bitte fordern Sie einen neuen Link
                an.
              </Text>
            ) : (
              <>
                <Text
                  type="secondary"
                  style={{
                    display: "block",
                    textAlign: "center",
                    marginBottom: 16,
                  }}
                >
                  Bitte geben Sie das Passwort ein, das Sie erhalten haben.
                </Text>
                <Input.Password
                  placeholder="Passwort eingeben"
                  value={password}
                  size="large"
                  onChange={(e) => setPassword(e.target.value)}
                  onPressEnter={handleLogin}
                  style={{ marginBottom: 16, borderRadius: 8 }}
                />
                <ActionButton
                  type="primary"
                  block
                  size="large"
                  loading={loginLoading}
                  onClick={handleLogin}
                >
                  Anmelden
                </ActionButton>
              </>
            )}
          </LoginBox>
        </LoginPage>
      );
    }

    const handleExportCsv = () => {
      if (!data) return;
      const csv = toCsv(data, tenantTitle);
      const from = dateRange[0].format("YYYY-MM-DD");
      const to = dateRange[1].format("YYYY-MM-DD");
      const safeName = tenantTitle.replace(/[^a-zA-Z0-9äöüÄÖÜß\-_ ]/g, "");
      downloadCsv(csv, `Analytics_${safeName}_${from}_${to}.csv`);
    };

    return (
      <PageWrapper>
        <DashboardContainer>
          <DashboardHeader>
            <HeaderLeft>
              <h2>Analytics Dashboard</h2>
              <Tag color="blue" style={{ fontSize: 14, padding: "2px 12px" }}>
                {tenantTitle}
              </Tag>
            </HeaderLeft>
            <FilterBar>
              <RangePicker
                value={dateRange}
                onChange={(v) => {
                  if (v && v[0] && v[1]) setDateRange([v[0], v[1]]);
                }}
                size="large"
              />
            </FilterBar>
          </DashboardHeader>

          <Space style={{ marginBottom: 24 }} wrap>
            <ActionButton onClick={handleExportCsv} size="middle" disabled={!data}>
              CSV Export
            </ActionButton>
          </Space>

          {data && (
            <AnalyticsDashboardView
              data={data}
              loading={loading}
              groupMode={groupMode}
              onGroupModeChange={setGroupMode}
            />
          )}
        </DashboardContainer>
      </PageWrapper>
    );
  }
);
